import { YStack, Text, Form, XStack } from "tamagui";
import { colors } from "@styles/colors";
import api, { handleRequest } from "@services/axios/apiManager";
import FormItem from "@components/form/FormItem";
import { useForm, FormProvider, FieldErrors, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import toast from "react-hot-toast";
import { toToastMessage } from "@utils/toastMessage";
import { AxiosError, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import { AddressComponent, handleAPIAddressComponents } from "@components/address/API/handleAddressAPI";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useUser } from "@utils/store/user/useUser";
import { getFamilyInviteCode, clearFamilyInviteCode } from "@utils/familyInvite";
import { getShopInviteCode, clearShopInviteCode } from "@utils/shopInviteCode";
import { getReferredCourierId, getReferredOrderId, clearReferredCourierId } from "@utils/referredCourier";
import { cleanKeyword } from "@components/address/func/addressTextTool";
import { validateTaxIdNumber } from "@components/form/validate/validateTaxIdNumber";
import { validateInvoiceCarrier } from "@components/form/validate/validateInvoiceCarrier";
import { validateTaiwanMobile, validateTaiwanPhone } from "@components/form/validate/vaildateTaiwanPhone";
import { getLineProfileByToken } from "@components/auth/Line/lineProfile";
import { useNavigate } from "react-router-dom";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { RiUserLine, RiMapPinLine, RiPhoneLine, RiFileList3Line, RiCheckboxCircleFill } from "react-icons/ri";
import { track } from "@lib/tracker";
import {
  regAddress,
  regAgree,
  regBirth,
  regContactPhone,
  regGender,
  regInvoiceCarrier,
  regInvoiceType,
  regName,
  regPhoneNumber,
  regTaxIdNumber,
} from "./registerFormItem";

const regInvoiceTypeData = {
  I: regInvoiceCarrier, //-- 手機條碼
  T: regTaxIdNumber, //-- 公司統編
  O: null, //-- 其他(不需要)
};

const Step3 = () => {
  const navigate = useNavigate();
  const { lineAccessToken } = useLineAuth();
  const { setLoading } = useUtilsStore();
  const { invGasData } = useUser();
  // 平台層級設定：此 supplier 是否顯示發票填寫區（PW 後台控制）。opt-in：未設定 / 未取得時一律不顯示。
  const showInvoice = invGasData?.supplier?.show_invoice === true;

  //@ 建立 Form
  const formSchema = z
    .object({
      name: z.string().trim().min(1, { message: "請填寫姓名" }),
      phoneNumber: z
        .string()
        .regex(/^09\d{8}$/, { message: "請輸入有效的手機號碼 (09開頭共10碼)" })
        .refine((val) => !invGasData?.verifiedPhone || val === invGasData.verifiedPhone, {
          message: "此號碼必須與已驗證手機相同",
        }),
      contactPhone: z.array(
        z.object({
          contactPhoneId: z.number().optional().nullable(),
          contactPhone: z.string().min(1, { message: "請輸入聯絡電話" }).refine(validateTaiwanPhone, {
            message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)",
          }),
        }),
      ),
      // 發票區被平台關閉時不給選 → invoiceType 不強制（見 superRefine）。
      invoiceType: z.string().trim(),
      taxIdNumber: z
        .string()
        .nullish()
        .refine(
          (val) => {
            if (!val || val === "") return true;
            return validateTaxIdNumber(val);
          },
          {
            message: "請輸入有效的統一編號",
          },
        ),
      invoiceCarrier: z
        .string()
        .transform((val) => val.toUpperCase()) // 轉換成大寫
        .nullish()
        .refine(
          (val) => {
            if (!val || val === "") return true;
            return validateInvoiceCarrier(val);
          },
          {
            message: "請輸入有效的載具條碼",
          },
        ),
      gender: z.string().trim().min(1, { message: "請選擇性別" }),
      birth: z.string().trim().min(1, { message: "請填寫生日" }),
      isAddress: z
        .array(
          z.object({
            addressId: z.number().optional().nullable(),
            city: z.string().min(1, "請選擇城市"),
            area: z.string().min(1, "請選擇區域"),
            road: z.string().min(1, "請輸入路名"),
            streetNumber: z.string().min(1, "請輸入門牌號"),
            floor: z.string().optional(),
            room: z.string().optional(),
            otherAddress: z.string().optional(),
            latitude: z.number().optional().nullable(),
            longitude: z.number().optional().nullable(),
          }),
        )
        .min(1, "請填寫配送地址"),
      agree: z.boolean().refine((val) => val === true, {
        message: "請閱讀並同意使用者協議",
      }),
    })
    .superRefine((data, ctx) => {
      // 平台關閉發票區 → 跳過所有發票驗證（完全不需發票）。
      if (!showInvoice) return;
      if (!data.invoiceType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "請選擇類型",
          path: ["invoiceType"],
        });
        return;
      }
      if (data.invoiceType === "I" && !data.invoiceCarrier) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "請輸入載具條碼",
          path: ["invoiceCarrier"],
        });
      }

      if (data.invoiceType === "T" && !data.taxIdNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "請輸入統一編號",
          path: ["taxIdNumber"],
        });
      }
    });
  type FormData = z.infer<typeof formSchema>;

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      phoneNumber: "",
      contactPhone: [],
      invoiceType: "",
      taxIdNumber: "",
      invoiceCarrier: "",
      gender: "",
      birth: "",
      isAddress: [],
      agree: false,
    },
  });

  const {
    control,
    formState: { errors },
  } = methods;

  //@ 處理滾動至錯誤位置
  const onInvalid = (errors: FieldErrors<FormData>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const firstErrorKey = errorKeys[0];
      const element = document.getElementsByName(firstErrorKey)[0] || document.getElementById(firstErrorKey);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        const fallbackElement = document.querySelector(`[id*="${firstErrorKey}"]`);
        fallbackElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  //@ 監聽
  const watchName = useWatch({ control, name: "name" });
  const watchInvoiceType = useWatch({ control, name: "invoiceType" }) || "";
  const getInvoiceData = regInvoiceTypeData[watchInvoiceType as keyof typeof regInvoiceTypeData];

  const onSubmit = async (data: FormData) => {
    const getInvoiceFields = (invoiceType: string, data: FormData) => {
      if (invoiceType === "I") return { invoiceCarrier: data.invoiceCarrier ?? "", invoice_type: "3J0002", taxIdNumber: "" };
      if (invoiceType === "T") return { taxIdNumber: data.taxIdNumber ?? "", invoiceCarrier: "", invoice_type: "" };
      return { taxIdNumber: "", invoiceCarrier: "", invoice_type: "" };
    };
    const postData = {
      verifiedTicket: invGasData?.verifiedTicket ?? null,
      phoneVerifiedTicket: invGasData?.phoneVerifiedTicket ?? null,
      accessToken: lineAccessToken,
      linebotUser: {
        name: data?.name,
        phoneNumber: data?.phoneNumber,
        gender: data?.gender,
        birth: data?.birth,
      },
      customer: {
        mainPhone: data?.phoneNumber,
        contactPhone: data.contactPhone.map((item) => ({
          contactPhoneId: item?.contactPhoneId || undefined,
          phone: item?.contactPhone,
        })),
        address: data.isAddress.map((item) => ({
          address_id: item?.addressId || undefined,
          city: item?.city || null,
          site_id: item?.area || null,
          village: null,
          neighborhood: null,
          road: item?.road || null,
          section: null,
          lane: null,
          alley: null,
          address_number: item?.streetNumber ? `${item?.streetNumber}號` : null,
          floor: item?.floor || null,
          room: item?.room || null,
          extra: item?.otherAddress || null,
        })),
        ...getInvoiceFields(data.invoiceType, data),
      },
      // scenario 1：掃 QR 當下沒有 customer → 註冊建立 customer 後，後端用此邀請碼立即加入家庭 context。
      // 無邀請碼時不帶（undefined 不會出現在 JSON）。
      invite_code: getFamilyInviteCode() ?? undefined,
      // 瓦斯行邀請碼（shopCode）：驗證 onboarding 入口宣稱的瓦斯行與後端 CIS 推導出的瓦斯行是否一致。
      // 無 shopCode 時不帶（undefined 不會出現在 JSON）。
      shop_code: getShopInviteCode() ?? undefined,
      // 引導註冊的配送員（DAPP 序號連結 ?courierId=）：後端以 DynamoDB 邀請碼記錄為主、此值為備援；
      // 後端會驗證隸屬瓦斯行，不符只忽略不擋註冊。無值時不帶。
      courier_id: getReferredCourierId() ?? undefined,
      // 客戶掃的是哪一張訂單的 QR（連結 ?orderId=）：後端驗證訂單屬於該 CIS，不符只忽略。無值時不帶。
      order_id: getReferredOrderId() ?? undefined,
    };

    track("funnel_step", { funnel: "register", step: "profile_submit" });
    handleRequest({
      asyncRequest: () => api.post("/app-api/line/register", postData, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        // register 漏斗：註冊完成（後端 /line/register 成功）
        track("funnel_step", { funnel: "register", step: "register_complete" });
        setLoading(true);
        localStorage.setItem("userData", JSON.stringify(res.data));
        Cookies.remove("invite");
        clearFamilyInviteCode();
        clearShopInviteCode();
        clearReferredCourierId();
        window.location.href = res?.data?.data?.redirectUrl;
      },
      errorCallback: (error: any) => {
        if (error?.response?.data?.message === "驗證票證不存在或已失效") {
          toast.error("驗證票證不存在或已失效，請重新完成驗證");
          navigate("/register/step2/inviteCode");
        } else {
          error && toast.error(toToastMessage(error.response?.data?.message, "註冊失敗"));
        }
      },
      setLoading,
    });
  };

  //API - 地址切分
  const handleAddressComponents = async (
    addressList: {
      addressId: string | number | null | undefined;
      fullAddress: string;
      latitude: number;
      longitude: number;
    }[],
    saveStore: boolean,
  ) => {
    setLoading(true);
    const requiredList: string[] = addressList.map((item) => item.fullAddress);
    const res = await handleAPIAddressComponents(requiredList, lineAccessToken || "");
    if (res) setLoading(false);
    if (res?.state === "success") {
      const addressComponents = res.data as AddressComponent[];

      if (saveStore) {
        methods.setValue<"isAddress">(
          "isAddress",
          addressComponents
            .filter((fItem) => {
              return fItem.administrative_area_level_1 && fItem.administrative_area_level_2;
            })
            .map((item, index) => ({
              addressId: addressList[index]?.addressId ? Number(addressList[index].addressId) : null,
              city: item.administrative_area_level_1 || "",
              area: item.administrative_area_level_2 || "",
              road: item.route || "",
              streetNumber: cleanKeyword(item.street_number || "", "號"),
              room: cleanKeyword(item.room || "", "室"),
              floor: cleanKeyword(item.floor || "", "樓"),
              latitude: addressList[index]?.latitude ? Number(addressList[index].latitude) : undefined,
              longitude: addressList[index]?.longitude ? Number(addressList[index].longitude) : undefined,
            })),
          { shouldDirty: true },
        );
        return true;
      }
      return res.data;
    }
    return null;
  };

  const getLineProfile = async (lineAccessToken: string) => {
    const res = await getLineProfileByToken(lineAccessToken || "");
    if (res.status === "success") {
      const profileData = res.data;
      methods.setValue("name", profileData.displayName || "", { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (invGasData && lineAccessToken) {
      getLineProfile(lineAccessToken);
      setLoading(false);
      try {
        //-- 自動帶入處理客戶地址 - 地址正規化
        //-- 防止沒有的情況下
        if ((invGasData?.customerInfo?.deliveryAddressList?.length ?? 0) > 0) {
          handleAddressComponents(invGasData?.customerInfo?.deliveryAddressList ?? [], true);
        }
        //-- 處理 mapping 資料 - 自動帶入;
        // customer.mainPhone 必須等於 OTP 驗證過的電話（backend 401 phone mismatch
        // invariant，見 app-line-login-phone-otp-spec.md §5.3 / §九 #1）。
        // 因此 phoneNumber pre-fill 來源是 OTP 驗過的 verifiedPhone，不是
        // customerInfo.mainPhone（後者可能是 invite-code lookup 帶來的 DB 既有值，
        // 與 OTP 驗過的電話不一定相同）。
        const verifiedPhone = invGasData?.verifiedPhone || "";
        const updateData = {
          //-- 判斷預設發票類型(預設有invoiceCarrier則帶"手機條碼",否則則帶"公司統編"為預設選擇)
          //-- 平台關閉發票區時不預填類型（保持空字串，送單不帶發票）
          invoiceType: !showInvoice ? "" : invGasData?.customerInfo?.invoiceCarrier ? "I" : "T",
          phoneNumber: validateTaiwanMobile(verifiedPhone) ? verifiedPhone : "",
          taxIdNumber: invGasData?.customerInfo?.taxIdNumber || "",
          invoiceCarrier: invGasData?.customerInfo?.invoiceCarrier || "",
        };

        (Object.keys(updateData) as Array<keyof typeof updateData>).forEach((key) => {
          const value = updateData[key];
          methods.setValue(key as keyof FormData, value as never, { shouldDirty: true });
        });
      } catch (e) {
        console.error("解析 JSON 失敗", e);
      }
    } else {
      toast.error("錯誤，請重新輸入邀請碼");
      setTimeout(() => {
        navigate("/register/step2/inviteCode");
      }, 1000);
    }
  }, [invGasData, lineAccessToken]);

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit, onInvalid)} gap="$4">
          <RadiusCard>
            <RadiusBoxLabel label="個人資料" icon={<RiUserLine color={colors.primary} />} />
            <YStack id={regName.data?.id}>
              <FormItem itemType={regName?.itemType} required={regName?.required} data={regName?.data} />
              {watchName && (
                <Text fontSize={14} color={colors.primary} style={{ display: "flex", alignItems: "center" }} mt="$2">
                  <RiCheckboxCircleFill size={14} className="mr-1" />
                  已由 LINE 帶入,可直接修改
                </Text>
              )}
            </YStack>
            <YStack id={regGender.data?.id}>
              <FormItem itemType={regGender?.itemType} required={regGender?.required} data={regGender?.data} />
            </YStack>
            <YStack id={regBirth.data?.id}>
              <FormItem itemType={regBirth?.itemType} required={regBirth?.required} data={regBirth?.data} />
            </YStack>
          </RadiusCard>
          <RadiusCard>
            <RadiusBoxLabel label="訂單聯絡電話" icon={<RiPhoneLine color={colors.primary} />} />
            <YStack id={regPhoneNumber.data?.id}>
              <FormItem itemType={regPhoneNumber?.itemType} required={regPhoneNumber?.required} data={regPhoneNumber?.data} />
            </YStack>
            <Text fontSize={12} color={colors.gray} mt="$2">
              訂單通知將傳送至此號碼
            </Text>
            <YStack id={regContactPhone.data?.id}>
              <FormItem itemType={regContactPhone?.itemType} required={regContactPhone?.required} data={regContactPhone?.data} />
            </YStack>
          </RadiusCard>
          <RadiusCard>
            <RadiusBoxLabel label="配送地址" icon={<RiMapPinLine color={colors.primary} />} />
            <YStack id={regAddress.data?.id} mt="$3">
              <FormItem itemType={regAddress?.itemType} required={regAddress?.required} data={regAddress?.data} />
            </YStack>
          </RadiusCard>
          {showInvoice && (
          <RadiusCard>
            <RadiusBoxLabel label="發票 / 載具" icon={<RiFileList3Line color={colors.primary} />} />
            <YStack id={regInvoiceType.data?.id} mt="$3">
              <FormItem
                itemType={regInvoiceType?.itemType}
                required={regInvoiceType?.required}
                data={{
                  ...regInvoiceType?.data,
                  options: regInvoiceType?.data.options.filter((item) => {
                    if (invGasData?.customerInfo?.invoiceCarrier) {
                      return true;
                    }
                    return item.value !== "I";
                  }),
                }}
              />
            </YStack>
            {getInvoiceData && (
              <YStack id={getInvoiceData.data?.id} mt="$3">
                <FormItem itemType={getInvoiceData?.itemType} required={getInvoiceData?.required} data={getInvoiceData?.data} />
              </YStack>
            )}
            {watchInvoiceType === "O" && (
              <Text fontSize={14} color={colors.gray} textAlign="center" pt="$5">
                將開立一般電子發票
              </Text>
            )}
          </RadiusCard>
          )}
          <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
            <Text fontSize={14} color={colors.gray}>
              ✨ 以上資料皆可在「我的帳號」中隨時修改
            </Text>
          </RadiusCard>
          <RadiusCard>
            <YStack id={regAgree.data?.id}>
              <FormItem itemType={regAgree?.itemType} required={regAgree?.required} data={regAgree?.data} />
            </YStack>
          </RadiusCard>
          <YStack backgroundColor={colors.bg} pt={8} style={{ position: "sticky", left: "50px", bottom: "0", width: "100%" }}>
            <XStack
              className="primary-bg-gradient"
              borderRadius={20}
              p={15}
              alignItems="center"
              justifyContent="center"
              onPress={methods.handleSubmit(onSubmit, onInvalid)}>
              <XStack gap={10}>
                <Text color="white" fontSize={16} fontWeight="bold">
                  完成註冊
                </Text>
              </XStack>
            </XStack>
          </YStack>
        </Form>
      </FormProvider>
    </>
  );
};

export default Step3;
